// ...existing code...
import { ApiClientService } from '@pw-tech/web-sdk/dist/types/src/services/api-client.service';
import { logger } from 'src/services/logger/loggerService';
import PwWebSDKInstance from 'src/services/pwWebSDKService';
import { handleServerTime } from 'src/utility';

import { handleApiError, NormalizedApiError } from './api-error-handler';

// Minimal RequestConfig shape for typing the wrapper methods
export type RequestConfig = {
  headers?: Record<string, any>;
  params?: Record<string, any>;
  public?: boolean; // to indicate if the request is public (no auth)
  omitHeaders?: boolean; // to indicate if default headers should be omitted
  [k: string]: any;
};

// Default headers for every WebSDK request.
//
// IMPORTANT: We intentionally do NOT include `client-id` here.
const DEFAULT_HEADERS: Record<string, any> = {
  'Content-Type': 'application/json',
  version: '0.0.1',
  'client-type': 'WEB',
  'client-version': 200,
};

// Create response object helper
const createResponseObject = (response: any, data: any = null, url: string) => ({
  status: response?.status,
  data: data?.data ?? data,
  headers: response?.headers,
  url: url,
});

class WebsdkApiClientService {
  private static instance: WebsdkApiClientService;
  private apiClient: ApiClientService | null = null;

  private constructor() {
    this.refreshClientReference();
  }

  public static getInstance(): WebsdkApiClientService {
    if (!WebsdkApiClientService.instance) {
      WebsdkApiClientService.instance = new WebsdkApiClientService();
    }
    return WebsdkApiClientService.instance;
  }

  private refreshClientReference() {
    this.apiClient = PwWebSDKInstance?.apiClient ?? null;
  }

  private getClientOrThrow(): ApiClientService {
    if (!this.apiClient) {
      this.refreshClientReference();
    }
    if (!this.apiClient) {
      throw new Error('PW WebSDK apiClient is not initialized');
    }
    return this.apiClient!;
  }

  private mergeRequestConfig(config?: RequestConfig): RequestConfig {
    return {
      ...(config || {}),
      headers: {
        ...DEFAULT_HEADERS,
        ...((config && config.headers) || {}),
      },

      public: config?.public ?? false,
      withStatus: true,
    };
  }

  private async requestPromise<T = any>(
    promise: Promise<any>,
    url: string,
    config?: RequestConfig,
  ): Promise<{
    status: number | undefined;
    data: any;
    headers: any;
    url: string | undefined;
  }> {
    try {
      const res = await promise;
      console.log('WebSDK apiClient response', res);
      if (res && typeof res === 'object' && 'status' in res) {
        return createResponseObject(res, res.data ?? res, url);
      }
      return createResponseObject(
        { status: undefined, headers: undefined, url: undefined },
        res,
        url,
      );
    } catch (err: any) {
      // const errorData = {
      //   status: err?.status ?? err?.response?.status,
      //   message: err?.message ?? err?.response?.data?.message,
      //   payload: err?.response?.data,
      //   endpoint: url,
      // };
      // const normalized = handleApiError(err, { hideToast: false, endpoint: url }, config);
      // return Promise.reject({ ...normalized });
      return handleApiError(err, { hideToast: false, endpoint: url }, config);
    }
  }

  // Generic typed methods that delegate to underlying apiClient and inject default headers
  public get = async <T = any>(
    url: string,
    config?: RequestConfig,
  ): Promise<{
    status: number | undefined;
    data: T | { error: NormalizedApiError };
    headers: any;
    url: string | undefined;
  }> => {
    const client = this.getClientOrThrow();
    const mergedConfig = this.mergeRequestConfig(config);
    console.log('WebsdkApiClientService GET called', { url, mergedConfig });
    return this.requestPromise<T>(client.get<T>(url, mergedConfig), url, mergedConfig);
  };

  public post = async <T = any>(
    url: string,
    data?: any,
    config?: RequestConfig,
  ): Promise<{
    status: number | undefined;
    data: T | { error: NormalizedApiError };
    headers: any;
    url: string | undefined;
  }> => {
    const client = this.getClientOrThrow();
    const mergedConfig = this.mergeRequestConfig(config);
    // ⭐ If the body is FormData → route to WebSDK.postMultipart
    if (data instanceof FormData) {
      // Explicitly set Content-Type to null for multipart requests
      const multipartConfig = {
        ...mergedConfig,
        headers: {
          ...mergedConfig.headers,
          'Content-Type': null,
        },
      };
      return this.requestPromise<T>(
        client.postMultipart<T>(url, data, multipartConfig),
        url,
        multipartConfig,
      );
    }

    // normal case
    return this.requestPromise<T>(client.post<T>(url, data, mergedConfig), url, mergedConfig);
  };

  public put = async <T = any>(
    url: string,
    data?: any,
    config?: RequestConfig,
  ): Promise<{
    status: number | undefined;
    data: T | { error: NormalizedApiError };
    headers: any;
    url: string | undefined;
  }> => {
    const client = this.getClientOrThrow();
    const mergedConfig = this.mergeRequestConfig(config);
    return this.requestPromise<T>(client.put<T>(url, data, mergedConfig), url, mergedConfig);
  };
  public patch = async <T = any>(
    url: string,
    data?: any,
    config?: RequestConfig,
  ): Promise<{
    status: number | undefined;
    data: T | { error: NormalizedApiError };
    headers: any;
    url: string | undefined;
  }> => {
    const client = this.getClientOrThrow();
    const mergedConfig = this.mergeRequestConfig(config);
    return this.requestPromise<T>(client.patch<T>(url, data, mergedConfig), url, mergedConfig);
  };

  public delete = async <T = any>(
    url: string,
    config?: RequestConfig,
  ): Promise<{
    status: number | undefined;
    data: T | { error: NormalizedApiError };
    headers: any;
    url: string | undefined;
  }> => {
    const client = this.getClientOrThrow();
    const mergedConfig = this.mergeRequestConfig(config);
    return this.requestPromise<T>(client.delete<T>(url, mergedConfig), url, mergedConfig);
  };

  // allow external code to refresh the apiClient reference if SDK re-initializes
  public refresh() {
    this.refreshClientReference();
  }
}

export async function webSdkResponseGet(endpoint: string, customConfig?: RequestConfig) {
  console.log('webSdkResponseGet calleding outside', { endpoint, customConfig });
  try {
    const requestStartTime = Date.now();
    const response = await websdkApiClientService.get(endpoint, customConfig);
    const serverTime = response?.data?.serverTime;
    if (serverTime) {
      handleServerTime(serverTime, requestStartTime);
    }
    logger.log('websdkApiClientService response######', 'GET METHOD', response);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function webSdkResponsePost(
  endpoint: string,
  body?: any,
  customConfig?: RequestConfig,
) {
  try {
    const response = await websdkApiClientService.post(endpoint, body, customConfig);
    logger.log('websdkApiClientService response ', 'POST METHOD', response);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function webSdkResponsePut(
  endpoint: string,
  body?: any,
  customConfig?: RequestConfig,
) {
  try {
    const response = await websdkApiClientService.put(endpoint, body, customConfig);
    logger.log('websdkApiClientService response ', 'PUT METHOD', response);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function webSdkResponsePatch(
  endpoint: string,
  body?: any,
  customConfig?: RequestConfig,
) {
  try {
    const response = await websdkApiClientService.patch(endpoint, body, customConfig);
    logger.log('websdkApiClientService response ', 'PATCH METHOD', response);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function webSdkResponseDelete(endpoint: string, customConfig?: RequestConfig) {
  try {
    const response = await websdkApiClientService.delete(endpoint, customConfig);
    logger.log('websdkApiClientService response ', 'DELETE METHOD', response);
    return response;
  } catch (error) {
    throw error;
  }
}

// exported singleton instance
const websdkApiClientService = WebsdkApiClientService.getInstance();
export default websdkApiClientService;
